import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { viewCounterService } from '../../services';
import { formatNumber } from '../../utils/formatters';
import PageHeader from '../../components/common/PageHeader';

const PageViewCounters = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchCounters();
  }, []);

  const fetchCounters = async () => {
    try {
      setLoading(true);
      setError('');
      const res = await viewCounterService.getCounters();
      const payload = res?.data ?? res ?? {};
      setData({
        homepageViews: payload.homepageViews ?? 0,
        candidateViews: Array.isArray(payload.candidateViews) ? payload.candidateViews : [],
      });
    } catch (err) {
      console.error('Failed to fetch view counters', err);
      setError(err.response?.data?.message || 'Failed to load counters.');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-primary-50 pb-8">
      <PageHeader
        title="Page View Counters"
        subtitle="Simple view counts for homepage and Gurukul profiles"
      />
      <div className="max-w-5xl mx-auto px-4">
        <div className="mb-6">
          <Link
            to="/admin"
            className="inline-flex items-center text-gray-600 hover:text-gray-900 transition-colors"
          >
            <svg className="w-5 h-5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Back to Dashboard
          </Link>
        </div>

        {error && (
          <div className="mb-4 p-4 bg-red-50 text-red-700 rounded-lg">{error}</div>
        )}

        {data && (
          <div className="space-y-6">
            <div className="bg-white rounded-xl shadow-md p-6 border border-gray-100">
              <h2 className="text-lg font-semibold text-gray-800 mb-4">Homepage</h2>
              <div className="flex items-center gap-3">
                <span className="text-4xl font-bold text-primary-600">
                  {formatNumber(data.homepageViews)}
                </span>
                <span className="text-gray-600">views</span>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-md overflow-hidden border border-gray-100">
              <div className="px-6 py-4 border-b border-gray-200">
                <h2 className="text-lg font-semibold text-gray-800">Gurukul profile views</h2>
                <p className="text-sm text-gray-500 mt-1">Sorted by view count (highest first)</p>
              </div>
              {data.candidateViews.length === 0 ? (
                <div className="p-8 text-center text-gray-500">No Gurukul views recorded yet.</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-primary-50 border-b border-primary-100">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Gurukul
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Views
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {data.candidateViews.map((row) => (
                        <tr key={row.candidateId} className="hover:bg-primary-50/50 transition-colors">
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            {row.candidateName || row.candidateId || '—'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                            {formatNumber(row.viewCount)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PageViewCounters;
